<template>
  <HrPageLayout
    title="Training Evaluations"
    subtitle="Feedback and evaluation reports submitted by employees via My Training"
    icon="📋"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'Training Management Module' },
      { label: 'Training Evaluations' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-primary" :disabled="loading" @click="loadData">
        <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
      </button>
      <button type="button" class="hr-btn hr-btn-outline-success" :disabled="exporting || !rows.length" @click="exportCsv">
        <i class="bx bx-download"></i> CSV
      </button>
      <button type="button" class="hr-btn hr-btn-outline-success" :disabled="exportingPdf" @click="exportPdf">
        <i class="bx" :class="exportingPdf ? 'bx-loader-alt bx-spin' : 'bx-file'"></i> PDF
      </button>
    </template>

    <div class="te-page">
      <div class="te-stats">
        <div class="te-stat">
          <span class="te-stat-label">Total evaluations</span>
          <strong>{{ pagination.total }}</strong>
        </div>
        <div class="te-stat">
          <span class="te-stat-label">On this page</span>
          <strong>{{ rows.length }}</strong>
        </div>
      </div>

      <div class="te-filters">
        <label>
          <span>Search</span>
          <input v-model="filters.search" type="search" placeholder="Officer, emp No, training title…" @input="debouncedLoad">
        </label>
        <label>
          <span>Department</span>
          <select v-model.number="filters.departmentId" @change="onFilterChange">
            <option :value="0">All departments</option>
            <option v-for="d in departments" :key="d.id" :value="Number(d.id)">{{ d.departmentName }}</option>
          </select>
        </label>
        <label>
          <span>Per page</span>
          <select v-model.number="pagination.limit" @change="onFilterChange">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
          </select>
        </label>
      </div>

      <div class="hr-chart-card">
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p class="hr-text-muted">Loading evaluations from My Training…</p>
          </div>
          <div v-else-if="!rows.length" class="te-empty">
            <i class="bx bx-file-blank"></i>
            <h5>No evaluation reports yet</h5>
            <p>Employees submit post-training feedback under Self-service → My Training. Those submissions appear here as evaluations.</p>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Submitted</th>
                  <th>Officer</th>
                  <th>Emp No</th>
                  <th>Department</th>
                  <th>Training</th>
                  <th>Institution</th>
                  <th>Dates</th>
                  <th>Key learnings</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, idx) in rows" :key="r.id">
                  <td>{{ rowNum(idx) }}</td>
                  <td>{{ r.created_at || '—' }}</td>
                  <td><strong>{{ r.officer_name || '—' }}</strong></td>
                  <td>{{ r.emp_no || '—' }}</td>
                  <td>{{ r.employee_department_name || '—' }}</td>
                  <td>{{ r.training_title || '—' }}</td>
                  <td>{{ r.institution || '—' }}</td>
                  <td>{{ r.date_from || '—' }} → {{ r.date_to || '—' }}</td>
                  <td class="te-clip">{{ clip(r.key_learnings || r.content_summary || r.recommendations, 80) }}</td>
                  <td><span class="hr-badge hr-badge-info">{{ r.status || 'Submitted' }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="!loading && pagination.total > 0" class="te-pagination">
            <span>Showing {{ pageFrom }}–{{ pageTo }} of {{ pagination.total }}</span>
            <div class="te-page-btns">
              <button type="button" class="hr-btn hr-btn-sm" :disabled="!pagination.hasPrev" @click="goPage(pagination.page - 1)">Prev</button>
              <span>Page {{ pagination.page }} / {{ pagination.totalPages }}</span>
              <button type="button" class="hr-btn hr-btn-sm" :disabled="!pagination.hasNext" @click="goPage(pagination.page + 1)">Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { addCountyLetterhead, loadCountyLogoDataUrl } from "@/utils/payrollReconPdf";

export default {
  name: "TrainingEvaluations",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      exporting: false,
      exportingPdf: false,
      rows: [],
      departments: [],
      letterhead: null,
      debounceTimer: null,
      filters: { search: "", departmentId: 0 },
      pagination: { page: 1, limit: 25, total: 0, totalPages: 1, hasPrev: false, hasNext: false }
    };
  },
  computed: {
    pageFrom() {
      if (!this.pagination.total) return 0;
      return (this.pagination.page - 1) * this.pagination.limit + 1;
    },
    pageTo() {
      return Math.min(this.pagination.page * this.pagination.limit, this.pagination.total);
    }
  },
  mounted() {
    this.loadDepartments();
    this.loadLetterhead();
    this.loadData();
  },
  beforeUnmount() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  },
  methods: {
    rowNum(idx) {
      return (this.pagination.page - 1) * this.pagination.limit + idx + 1;
    },
    clip(t, n) {
      const s = String(t || "").replace(/\s+/g, " ").trim();
      return s.length > n ? s.slice(0, n) + "…" : s || "—";
    },
    debouncedLoad() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.pagination.page = 1;
        this.loadData();
      }, 350);
    },
    onFilterChange() {
      this.pagination.page = 1;
      this.loadData();
    },
    goPage(p) {
      this.pagination.page = Math.max(1, p);
      this.loadData();
    },
    async loadDepartments() {
      try {
        const fd = new FormData();
        fd.append("function", "getDepartments");
        const res = await execute(fd, authUrl);
        const payload = res?.data?.data;
        this.departments = Array.isArray(payload?.departments) ? payload.departments : [];
      } catch (_) {
        this.departments = [];
      }
    },
    async loadLetterhead() {
      try {
        const data = new FormData();
        data.append("function", "getCompanyInfo");
        const res = await execute(data, authUrl);
        if (res?.data?.success && res.data.data?.companyInfo) {
          const c = res.data.data.companyInfo;
          const abs = (u) => {
            if (!u) return "";
            if (/^https?:\/\//i.test(u)) return u;
            try { return new URL(u, authUrl).href; } catch (_) { return u; }
          };
          this.letterhead = {
            ...c,
            logoPrimaryUrl: abs(c.logoPrimaryUrl || c.logoPrimary),
            logoSecondaryUrl: abs(c.logoSecondaryUrl || c.logoSecondary)
          };
        }
      } catch (_) {
        this.letterhead = null;
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "listTrainingEvaluations");
        fd.append("page", String(this.pagination.page));
        fd.append("limit", String(this.pagination.limit));
        if (this.filters.search) fd.append("search", this.filters.search.trim());
        if (this.filters.departmentId) fd.append("departmentId", String(this.filters.departmentId));
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          const d = res.data.data || {};
          this.rows = d.evaluations || d.reports || [];
          const p = d.pagination || {};
          this.pagination = {
            page: Number(p.page || this.pagination.page),
            limit: Number(p.limit || this.pagination.limit),
            total: Number(p.total || 0),
            totalPages: Number(p.totalPages || 1),
            hasPrev: !!p.hasPrev,
            hasNext: !!p.hasNext
          };
        } else {
          this.rows = [];
        }
      } catch (_) {
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    exportCsv() {
      this.exporting = true;
      try {
        const headers = ["Submitted", "Officer", "Emp No", "Department", "Training", "Institution", "From", "To", "Key learnings", "Status"];
        const body = this.rows.map((r) => [
          r.created_at || "",
          r.officer_name || "",
          r.emp_no || "",
          r.employee_department_name || "",
          r.training_title || "",
          r.institution || "",
          r.date_from || "",
          r.date_to || "",
          (r.key_learnings || r.content_summary || "").toString().replace(/\n/g, " "),
          r.status || ""
        ]);
        const csv = [headers, ...body].map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `training_evaluations_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } finally {
        this.exporting = false;
      }
    },
    async exportPdf() {
      this.exportingPdf = true;
      try {
        if (!this.letterhead) await this.loadLetterhead();
        const logoDataUrl = await loadCountyLogoDataUrl(this.letterhead);
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const startY = addCountyLetterhead(doc, {
          title: "TRAINING EVALUATIONS REPORT",
          subtitle: "Feedback submitted via My Training",
          periodLabel: new Date().toLocaleString("en-KE"),
          logoDataUrl,
          letterhead: this.letterhead
        });
        autoTable(doc, {
          startY,
          head: [["#", "Date", "Officer", "Emp No", "Dept", "Training", "Institution", "Status"]],
          body: this.rows.map((r, i) => [
            String(this.rowNum(i)),
            r.created_at || "",
            r.officer_name || "",
            r.emp_no || "",
            r.employee_department_name || "",
            r.training_title || "",
            r.institution || "",
            r.status || ""
          ]),
          styles: { fontSize: 7 },
          headStyles: { fillColor: [12, 74, 110], textColor: 255 },
          margin: { left: 10, right: 10 }
        });
        doc.save(`training_evaluations_${new Date().toISOString().slice(0, 10)}.pdf`);
      } catch (e) {
        window.alert(e?.message || "PDF failed");
      } finally {
        this.exportingPdf = false;
      }
    }
  }
};
</script>

<style scoped>
.te-page { display: flex; flex-direction: column; gap: 1rem; }
.te-stats { display: flex; gap: 1rem; flex-wrap: wrap; }
.te-stat {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
  padding: 0.9rem 1.2rem; min-width: 140px;
}
.te-stat-label { display: block; font-size: 0.75rem; color: #64748b; font-weight: 600; text-transform: uppercase; }
.te-stat strong { font-size: 1.4rem; color: #0f172a; }
.te-filters {
  display: grid; grid-template-columns: 2fr 1fr 100px; gap: 0.75rem;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 0.9rem 1rem;
}
.te-filters label { display: flex; flex-direction: column; gap: 0.35rem; font-size: 0.75rem; font-weight: 600; color: #64748b; }
.te-filters input, .te-filters select {
  border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.55rem 0.7rem; background: #f8fafc; font-size: 0.9rem;
}
.te-empty { text-align: center; padding: 3rem 1rem; color: #64748b; }
.te-empty i { font-size: 3rem; color: #94a3b8; }
.te-clip { max-width: 220px; font-size: 0.85rem; color: #475569; }
.te-pagination {
  display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.75rem;
  margin-top: 1rem; font-size: 0.85rem; color: #64748b; font-weight: 600;
}
.te-page-btns { display: flex; align-items: center; gap: 0.5rem; }
@media (max-width: 768px) {
  .te-filters { grid-template-columns: 1fr; }
}
</style>
